import React, { Component, ReactNode } from 'react';
import ItemToolsMenu from '../../ItemToolsMenu';
import CustomSidebarLayout from '../../custom/CustomSidebarLayout';
import { Group, Role, User } from '../../../domain/rbac/RbacDomains';
import GroupListComponent from './GroupListComponent';
import CustomLoading from '../../custom/CustomLoading';
import GroupEditComponent from './GroupEditComponent';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import GroupService from '../../../service/GroupService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import _ from 'lodash';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import RoleHelper from '../../../RoleHelper';
import ComponentName from '../../../constant/ComponentName';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import { CustomFilterFields } from '../../../domain/ui/FilterDomains';
import FilterUtil from '../../../util/FilterUtil';
import { dispatch } from '../../../redux/store';
import { changePageFilter } from '../../../redux/actions/PageActions';
import ValueFactory from '../../../util/ValueFactory';
import empty = query.empty;
import Filter = query.Filter;
import eq = query.eq;
import and = query.and;
import { CheckerRequest } from '../../../domain/maker-checker/MakerCheckerDomains';
import QueryBuilder = query.QueryBuilder;
import LanguageUtil from '../../../util/LanguageUtil';

class GroupContainer extends Component<Props, State> {
  public state = new State();

  private groupSubscription: subscribe.Subscription | undefined;
  private rolesSubscription: subscribe.Subscription | undefined;
  private userSubscription: subscribe.Subscription | undefined;
  private checkerRequestSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly groupService: GroupService = ServiceContainer.getInstance().get(ServiceNames.GROUP);
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  public render(): ReactNode {
    const { visible, loading, previewUserUpdateRequest, checkerRequests, selectedGroup, isCreate, groups, roles, users, rows, currentPage, filter } =
      this.state;
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col w-full h-full px-5'}>
        <div className={'flex min-h-[24px] justify-start bg-white items-center w-full'}>
          <ItemToolsMenu
            createFunc={this.openCreatePage}
            onChangeFilter={this.onChangeFilter}
            filter={filter}
            component={ComponentName.ADMIN_GROUP}
            defaultFilterDisabled={true}
            exportVisible={false}
            importVisible={false}
            createVisible={!this.environmentUtil.isLdapOnly()}
            componentLabel={LanguageUtil.get({ id: 'pages.admin.group.title', defaultMessage: 'Group Management' })}
          />
        </div>
        <div className={'flex w-full h-full'}>
          <div className={'w-full h-full flex flex-col lg:!max-h-[calc(100vh-20rem)] overflow-y-scroll'}>
            <GroupListComponent
              selectedGroup={selectedGroup}
              openEditor={this.openUpdatePage}
              setSelectedGroup={this.setSelectedGroup}
              groups={groups}
              onDeleteGroup={this.onDeleteGroup}
              updateRequestData={previewUserUpdateRequest}
              setUpdateRequestData={(data: CheckerRequest<User> | undefined) => this.setState({ previewUserUpdateRequest: data })}
              checkerRequests={checkerRequests}
            />
            <CustomPaginator
              rows={rows}
              currentPage={currentPage}
              rowsPerPageOptions={[1, 5, 10, 20, 50, 100, 200]}
              totalRecords={this.state.totalRecord}
              currentRecords={groups.length}
              onPageChange={this.onChangePage}
            />
          </div>
          {visible && <div className={'absolute top-0 left-0 w-full h-full bg-[#D9D9D94D]'}></div>}
          <CustomSidebarLayout
            icon={'users'}
            content={
              <GroupEditComponent
                users={users}
                roles={roles}
                isCreate={isCreate}
                onChangeGroup={this.setSelectedGroup}
                selectedGroup={selectedGroup}
                checkerRequests={checkerRequests}
                onHide={this.onChangeVisible}
              />
            }
            title={LanguageUtil.get({ id: 'pages.admin.group.editor.title', defaultMessage: 'Group Editor' })}
            visible={visible}
            closable={false}
            onHide={this.onChangeVisible}
          />
          {previewUserUpdateRequest ? (
            <CustomSidebarLayout
              icon={'users'}
              key='preview'
              content={
                <GroupEditComponent
                  users={users}
                  roles={roles}
                  isCreate={isCreate}
                  onChangeGroup={this.setSelectedGroup}
                  selectedGroup={previewUserUpdateRequest.metadata as Group}
                  onHide={this.onChangeVisible}
                  checkerRequests={checkerRequests}
                  preview
                />
              }
              title={'Group'}
              visible={visible}
              closable={false}
              onHide={this.onChangeVisible}
              position='left'
            />
          ) : null}
        </div>
      </div>
    );
  }

  public componentDidMount() {
    this.prepareGroupCount();
    this.prepareFilterFields();
    this.startTracker();
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const { rows, filter, currentPage } = this.state;
    const groupQuery: query.Query = query.QueryBuilder.select()
      .from(CollectionNames.GROUPS)
      .limit(rows)
      .where(filter)
      .skip((currentPage - 1) * rows)
      .build();
    const roleQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ROLES).where(empty()).build();
    const userQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.USERS).where(empty()).build();
    const checkerRequestQuery: query.Query = query.QueryBuilder.select()
      .from(CollectionNames.CHECKER_REQUESTS)
      .where(and(eq('component', ComponentName.ADMIN_GROUP), eq('status', 'Waiting')))
      .build();
    this.groupSubscription = subscribe.Formica.subscribe(CollectionNames.GROUPS, groupQuery);
    this.rolesSubscription = subscribe.Formica.subscribe(CollectionNames.ROLES, roleQuery);
    this.userSubscription = subscribe.Formica.subscribe(CollectionNames.USERS, userQuery);
    this.checkerRequestSubscription = subscribe.Formica.subscribe(CollectionNames.CHECKER_REQUESTS, checkerRequestQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [
      this.groupSubscription,
      this.rolesSubscription,
      this.userSubscription,
      this.checkerRequestSubscription,
    ]);
  };

  private tracker = async () => {
    const { rows, filter } = this.state;
    if (!this.groupSubscription?.ready() || !this.rolesSubscription?.ready() || !this.userSubscription?.ready()) {
      return;
    }
    await this.prepareGroupCount();
    const groupQuery: query.Query = query.QueryBuilder.select().limit(rows).from(CollectionNames.GROUPS).where(filter).build();
    const roleQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ROLES).build();
    const userQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.USERS).build();
    const checkerRequestQuery = QueryBuilder.select().from(CollectionNames.CHECKER_REQUESTS).build();
    const groups: Group[] = await this.repository.findMany(groupQuery);
    const roles: Role[] = await this.repository.findMany(roleQuery);
    const users: User[] = await this.repository.findMany(userQuery);
    const checkerRequests: CheckerRequest[] = await this.repository.findMany(checkerRequestQuery);
    this.setState({ groups, roles, users, checkerRequests, loading: false });
  };

  private stopTracker = async () => {
    await this.userSubscription?.stop();
    await this.groupSubscription?.stop();
    await this.rolesSubscription?.stop();
    await this.checkerRequestSubscription?.stop();
    this.computation?.stop();
  };

  private reSubscribe = async () => {
    if (this.computation != undefined) {
      this.computation.onStop(() => this.startTracker());
      await this.stopTracker();
    } else {
      await this.stopTracker();
      this.startTracker();
    }
  };

  private prepareFilterFields = async () => {
    const fields: CustomFilterFields[] = await FilterUtil.getFields(ComponentName.ADMIN_GROUP);
    dispatch(
      changePageFilter({
        filter: ValueFactory.initializeFilter(),
        items: fields,
      }),
    );
  };

  private prepareGroupCount = async () => {
    const { filter } = this.state;
    try {
      const _query = query.QueryBuilder.select().from(CollectionNames.GROUPS).where(filter).build();
      const count = await this.groupService.getCount(_query);
      this.setState({ totalRecord: count });
    } catch (e) {}
  };

  private onDeleteGroup = async (name: string) => {
    await this.groupService.deleteGroup(name);
    await this.prepareGroupCount();
  };

  public onChangePage = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows, currentPage: event.page }, this.reSubscribe);
  };

  private onChangeVisible = (): void => {
    const { isCreate } = this.state;
    if (!RoleHelper.canManage('Group') && !isCreate) return;
    this.setState({ visible: false, selectedGroup: ValueFactory.initializeGroup() });
  };

  private setSelectedGroup = (group: Group) => {
    this.setState({ selectedGroup: group });
  };

  private openCreatePage = () => {
    if (!RoleHelper.canManage('Group')) return;
    this.setState({ isCreate: true, visible: true });
  };

  private openUpdatePage = () => {
    this.setState({ isCreate: false, visible: true });
  };

  private onChangeFilter = (filter: Filter) => {
    const isSame = _.isEqual(filter, this.state.filter);
    if (isSame) return;
    this.setState({ filter }, this.reSubscribe);
  };
}

interface Props {}

class State {
  groups: Group[] = [];
  users: User[] = [];
  roles: Role[] = [];
  checkerRequests: CheckerRequest[] = [];

  visible = false;
  loading = true;
  isCreate = false;
  rows = 10;
  totalRecord = 10;
  currentPage = 1;
  selectedGroup: Group | any = ValueFactory.initializeGroup();
  filter: Filter = empty();

  previewUserUpdateRequest: CheckerRequest<User> | undefined;
}

export default GroupContainer;
